/**
 * Lazy-loaded page imports (STEP 3/15) — each page gets its own code-
 * split chunk. Foundation pages only (per this stage's own scope);
 * every future HMIS module's own pages register here the same way.
 */
import { lazy } from 'react';

export const LoginPage = lazy(() => import('@app/pages/LoginPage').then((m) => ({ default: m.LoginPage })));
// DashboardPage: F0's own placeholder (`app/pages/DashboardPage.tsx`) is replaced by the real
// Enterprise Command Center (Stage F5) — same `/` route, no routing change, new implementation.
export const DashboardPage = lazy(() =>
  import('@features/dashboard/pages/EnterpriseDashboardPage').then((m) => ({ default: m.EnterpriseDashboardPage })),
);
export const NotFoundPage = lazy(() => import('@app/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));
export const ServerErrorPage = lazy(() => import('@app/pages/ServerErrorPage').then((m) => ({ default: m.ServerErrorPage })));
export const ForbiddenPage = lazy(() => import('@app/pages/ForbiddenPage').then((m) => ({ default: m.ForbiddenPage })));
export const MaintenancePage = lazy(() => import('@app/pages/MaintenancePage').then((m) => ({ default: m.MaintenancePage })));
// Enterprise Navigation Overhaul — one shared placeholder for every not-yet-built module leaf.

// Patient module (Stage F1)
export const PatientSearchPage = lazy(() => import('@features/patient/pages/PatientSearchPage').then((m) => ({ default: m.PatientSearchPage })));
export const PatientRegistrationPage = lazy(() =>
  import('@features/patient/pages/PatientRegistrationPage').then((m) => ({ default: m.PatientRegistrationPage })),
);
export const PatientProfilePage = lazy(() => import('@features/patient/pages/PatientProfilePage').then((m) => ({ default: m.PatientProfilePage })));
// ADR-034 — dedicated top-level route (not nested in Administration/Settings, see the page's own doc-comment).
export const RegistrationConfigurationPage = lazy(() =>
  import('@features/administration/pages/registration-configuration/RegistrationConfigurationPage').then((m) => ({
    default: m.RegistrationConfigurationPage,
  })),
);

// Appointment & Reception module (Stage F2)
// Single-page Enterprise Appointment Workstation redesign — the old
// 7-route ReceptionDashboardPage/AvailabilityCalendarPage/BookingPage/
// AppointmentLookupPage/CheckInPage/QueuePage/DoctorSchedulePage are
// kept on disk (their real logic was extracted into reusable
// `components/*` files this page composes) but deliberately no longer
// lazy-registered/routed — see AppRouter.tsx's redirects.
export const AppointmentWorkstationPage = lazy(() =>
  import('@features/appointment/pages/AppointmentWorkstationPage').then((m) => ({ default: m.AppointmentWorkstationPage })),
);

// Billing & Financial module (Stage F3)
export const BillingWorkstationPage = lazy(() =>
  import('@features/billing/pages/BillingWorkstationPage').then((m) => ({ default: m.BillingWorkstationPage })),
);

/**
 * OP Billing Workstation — Milestone B, Sprint 1. Mounted at `/op-billing`,
 * deliberately alongside `/billing` rather than replacing it: the existing
 * workstation is live and this one carries no billing capability yet.
 */
/** ADR-086 — the single Front Desk billing counter. Its own design language. */
export const UniversalBillingWorkstationPage = lazy(() =>
  import('@features/universal-billing/UniversalBillingWorkstationPage').then((m) => ({
    default: m.UniversalBillingWorkstationPage,
  })),
);

export const OpBillingWorkstationPage = lazy(() =>
  import('@features/op-billing/OpBillingWorkstationPage').then((m) => ({ default: m.OpBillingWorkstationPage })),
);

/** ADR-085 Phase 14 — the IP Billing Workstation (three-panel, keyboard-driven). */
export const IpBillingWorkstationPage = lazy(() =>
  import('@features/ip-billing/IpBillingWorkstationPage').then((m) => ({ default: m.IpBillingWorkstationPage })),
);
/** ADR-085 Phase 12's command centre — the payload existed, the screen did not. */
export const IpDashboardPage = lazy(() =>
  import('@features/ip-dashboard/IpDashboardPage').then((m) => ({ default: m.IpDashboardPage })),
);

/** The IP Registration Desk — the inpatient counterpart of `/patients/register`. */
export const IpDrugEntriesPage = lazy(() =>
  import('@features/ip-drug-entries/IpDrugEntriesPage').then((m) => ({ default: m.IpDrugEntriesPage })),
);
export const IpLookupPage = lazy(() =>
  import('@features/ip-lookup/IpLookupPage').then((m) => ({ default: m.IpLookupPage })),
);
export const IpRegistrationPage = lazy(() =>
  import('@features/ip-registration/IpRegistrationPage').then((m) => ({ default: m.IpRegistrationPage })),
);

/** HR & Workforce — Phase 1A Attendance. */
export const HrAttendancePage = lazy(() =>
  import('@features/hr/HrAttendancePage').then((m) => ({ default: m.HrAttendancePage })),
);

/** HR & Workforce — Phase 2A Recruitment (job requisition). */
export const HrRecruitmentPage = lazy(() =>
  import('@features/hr/HrRecruitmentPage').then((m) => ({ default: m.HrRecruitmentPage })),
);

/** HR & Workforce — Phase 3 Onboarding. */
export const HrOnboardingPage = lazy(() =>
  import('@features/hr/HrOnboardingPage').then((m) => ({ default: m.HrOnboardingPage })),
);

/** HR & Workforce — Phase 4 Training & Development. */
export const HrTrainingPage = lazy(() =>
  import('@features/hr/HrTrainingPage').then((m) => ({ default: m.HrTrainingPage })),
);

/** ADR-081 Phase 1 — one workstation for all four Finance Masters. */
export const FinanceConfigurationWorkstationPage = lazy(() =>
  import('@features/finance-config/FinanceConfigurationWorkstationPage').then((m) => ({
    default: m.FinanceConfigurationWorkstationPage,
  })),
);
export const BillingDashboardPage = lazy(() =>
  import('@features/billing/pages/BillingDashboardPage').then((m) => ({ default: m.BillingDashboardPage })),
);
export const PatientBillingWorkspacePage = lazy(() =>
  import('@features/billing/pages/PatientBillingWorkspacePage').then((m) => ({ default: m.PatientBillingWorkspacePage })),
);
export const CashierPage = lazy(() => import('@features/billing/pages/CashierPage').then((m) => ({ default: m.CashierPage })));
export const PackagesPricingPage = lazy(() =>
  import('@features/billing/pages/PackagesPricingPage').then((m) => ({ default: m.PackagesPricingPage })),
);
export const RefundsCreditNotesPage = lazy(() =>
  import('@features/billing/pages/RefundsCreditNotesPage').then((m) => ({ default: m.RefundsCreditNotesPage })),
);
export const OutstandingPage = lazy(() => import('@features/billing/pages/OutstandingPage').then((m) => ({ default: m.OutstandingPage })));
export const IpBillingPage = lazy(() => import('@features/billing/pages/IpBillingPage').then((m) => ({ default: m.IpBillingPage })));
// ADR-087 Phase 2 — LAB master-data catalogue workspace. Distinct from
// LabBillingPage below, which is a BILLING desk and stays one.
// ADR-088 Phase 10 — the LAB workflow workstation (order/specimen/result/verify).
export const LabWorkflowPage = lazy(() => import('@features/laboratory/LabWorkflowPage').then((m) => ({ default: m.LabWorkflowPage })));
// ADR-097 — the six LAB stages, each its own route, plus the order record.
export const LabOrdersPage = lazy(() => import('@features/laboratory/pages/LabOrdersPage').then((m) => ({ default: m.LabOrdersPage })));
/** Desk, 2026-08-26 — find any order, at any stage, however old. */
export const LabLookupPage = lazy(() => import('@features/laboratory/pages/LabLookupPage').then((m) => ({ default: m.LabLookupPage })));
/** Desk, 2026-09-25 — every lab order raised against one OP bill. */
export const LabTestsReportPage = lazy(() => import('@features/laboratory/reports/LabTestsReportPage').then((m) => ({ default: m.LabTestsReportPage })));
export const OpBillsEnquirePage = lazy(() => import('@features/laboratory/pages/OpBillsEnquirePage').then((m) => ({ default: m.OpBillsEnquirePage })));
/** Desk, 2026-09-25 — one IP stay's lab charges, by its bill number. */
export const IpBillEnquirePage = lazy(() => import('@features/laboratory/pages/IpBillEnquirePage').then((m) => ({ default: m.IpBillEnquirePage })));
export const LabPlaceOrderPage = lazy(() => import('@features/laboratory/pages/LabPlaceOrderPage').then((m) => ({ default: m.LabPlaceOrderPage })));
export const LabCollectionPage = lazy(() => import('@features/laboratory/pages/LabCollectionPage').then((m) => ({ default: m.LabCollectionPage })));
export const LabResultsPage = lazy(() => import('@features/laboratory/pages/LabResultsPage').then((m) => ({ default: m.LabResultsPage })));
export const LabVerificationPage = lazy(() => import('@features/laboratory/pages/LabVerificationPage').then((m) => ({ default: m.LabVerificationPage })));
export const LabReportsPage = lazy(() => import('@features/laboratory/pages/LabReportsPage').then((m) => ({ default: m.LabReportsPage })));
export const LabOrderRecordPage = lazy(() => import('@features/laboratory/pages/LabOrderRecordPage').then((m) => ({ default: m.LabOrderRecordPage })));
export const LabWorkspacePage = lazy(() => import('@features/laboratory/LabWorkspacePage').then((m) => ({ default: m.LabWorkspacePage })));
export const LabCataloguePage = lazy(() => import('@features/laboratory/LabCataloguePage').then((m) => ({ default: m.LabCataloguePage })));
export const LabBillingPage = lazy(() => import('@features/billing/pages/LabBillingPage').then((m) => ({ default: m.LabBillingPage })));
export const RadiologyBillingPage = lazy(() =>
  import('@features/billing/pages/RadiologyBillingPage').then((m) => ({ default: m.RadiologyBillingPage })),
);

// Clinical (EMR) module (Stage F4)
export const ClinicalDashboardPage = lazy(() =>
  import('@features/clinical/pages/ClinicalDashboardPage').then((m) => ({ default: m.ClinicalDashboardPage })),
);
export const EncounterWorkspacePage = lazy(() =>
  import('@features/clinical/pages/EncounterWorkspacePage').then((m) => ({ default: m.EncounterWorkspacePage })),
);
export const ClinicalDocumentsPage = lazy(() =>
  import('@features/clinical/pages/ClinicalDocumentsPage').then((m) => ({ default: m.ClinicalDocumentsPage })),
);
export const ClinicalDocumentViewPage = lazy(() =>
  import('@features/clinical/pages/ClinicalDocumentViewPage').then((m) => ({ default: m.ClinicalDocumentViewPage })),
);
export const ClinicalOrdersPage = lazy(() =>
  import('@features/clinical/pages/ClinicalOrdersPage').then((m) => ({ default: m.ClinicalOrdersPage })),
);
export const ClinicalTimelinePage = lazy(() =>
  import('@features/clinical/pages/ClinicalTimelinePage').then((m) => ({ default: m.ClinicalTimelinePage })),
);
export const ClinicalApprovalsPage = lazy(() =>
  import('@features/clinical/pages/ClinicalApprovalsPage').then((m) => ({ default: m.ClinicalApprovalsPage })),
);

// Enterprise Administration Portal (Phase G1)
export const AdministrationHubPage = lazy(() =>
  import('@features/administration/pages/AdministrationHubPage').then((m) => ({ default: m.AdministrationHubPage })),
);

// Enterprise Accounting Engine (Stage 13, Milestone 1)
export const FinanceHubPage = lazy(() =>
  import('@features/finance/pages/FinanceHubPage').then((m) => ({ default: m.FinanceHubPage })),
);

// Enterprise Pharmacy Management System (Stage 14, Phase 1)
export const PharmacyMasterHubPage = lazy(() =>
  import('@features/pharmacy/pages/PharmacyMasterHubPage').then((m) => ({ default: m.PharmacyMasterHubPage })),
);

// Enterprise Pharmacy Procurement & Inventory Engine (Stage 14, Phase 2)
export const PharmacyProcurementHubPage = lazy(() =>
  import('@features/pharmacy-procurement/pages/PharmacyProcurementHubPage').then((m) => ({ default: m.PharmacyProcurementHubPage })),
);

export const InsuranceHubPage = lazy(() =>
  import('@features/insurance/pages/InsuranceHubPage').then((m) => ({ default: m.InsuranceHubPage })),
);

export const GeneralStoresHubPage = lazy(() =>
  import('@features/general-stores/pages/GeneralStoresHubPage').then((m) => ({ default: m.GeneralStoresHubPage })),
);

// Enterprise Pricing & Commercial Engine (Stage 14, Phase 3)
export const PricingHubPage = lazy(() => import('@features/pricing/pages/PricingHubPage').then((m) => ({ default: m.PricingHubPage })));

// Enterprise Pharmacy Billing Workstation (Stage 15) — Phase 14 cutover:
// the old AG-Grid PharmacySalePage/PharmacyBillingHubPage are retired.
export const PharmacyBillingWorkstationPage = lazy(() =>
  import('@features/pharmacy-billing/pages/PharmacyBillingWorkstationPage').then((m) => ({ default: m.PharmacyBillingWorkstationPage })),
);

// Enterprise "My Profile" self-service module (ADR-033)
export const MyProfilePage = lazy(() => import('@features/profile/pages/MyProfilePage').then((m) => ({ default: m.MyProfilePage })));
export const CorrectionWorkflowPage = lazy(() =>
  import('@features/finance/pages/CorrectionWorkflowPage').then((m) => ({ default: m.CorrectionWorkflowPage })),
);
// Modifications → Correction (desk, 2026-10-07): the approvers' screen. Reached only through `CorrectionApproverRoute`.
export const DownloadPage = lazy(() => import('@features/data-export/pages/DownloadPage').then((m) => ({ default: m.DownloadPage })));
export const ModificationsCorrectionPage = lazy(() =>
  import('@features/modifications/pages/ModificationsCorrectionPage').then((m) => ({ default: m.ModificationsCorrectionPage })),
);

/**
 * MAIN COUNTER — the mother counter (desk, 2026-08-28). Three routes, one
 * workstation: the position across every counter, the settlement queue a
 * supervisor works at shift change, and cash/bank reconciliation.
 */
export const MainCounterPage = lazy(() => import('@features/main-counter/MainCounterPage').then((m) => ({ default: m.MainCounterPage })));
export const CounterSettlementPage = lazy(() => import('@features/main-counter/pages/CounterSettlementPage').then((m) => ({ default: m.CounterSettlementPage })));
export const CounterVerificationPage = lazy(() => import('@features/main-counter/pages/CounterVerificationPage').then((m) => ({ default: m.CounterVerificationPage })));
export const CounterReconciliationPage = lazy(() => import('@features/main-counter/pages/CounterReconciliationPage').then((m) => ({ default: m.CounterReconciliationPage })));
export const CounterReportsPage = lazy(() => import('@features/main-counter/pages/CounterReportsPage').then((m) => ({ default: m.CounterReportsPage })));
export const CounterCorrectionsReportPage = lazy(() => import('@features/main-counter/pages/CounterCorrectionsReportPage').then((m) => ({ default: m.CounterCorrectionsReportPage })));

/**
 * Reports & Analytics — the first standalone reports route in this application.
 *
 * Its own chunk, and deliberately not folded into any workstation's: the three
 * reports it carries read across Patient, Appointment, Financial and IP, so
 * there is no workstation that owns them. See the page's own header.
 */
export const ReportsAnalyticsPage = lazy(() =>
  import('@features/reports/ReportsAnalyticsPage').then((m) => ({ default: m.ReportsAnalyticsPage })),
);

/**
 * The Front Desk's own dashboard — the SAME report body `/reports?tab=front-desk`
 * renders, behind a route the desk's sidebar can point at directly.
 *
 * A separate lazy entry rather than a re-export of the page above, because the
 * two routes should not force each other's chunk: a receptionist who only ever
 * opens their dashboard does not need the collection and consultation reports
 * parsed, and the shared parts (`FrontDeskOverviewTab` and the report
 * components) are hoisted by the bundler into a chunk both import.
 */
export const FrontDeskDashboardPage = lazy(() =>
  import('@features/reports/pages/FrontDeskDashboardPage').then((m) => ({ default: m.FrontDeskDashboardPage })),
);

// DISCHARGE MODULE (owner, 2026-10-23).
export const DischargeDashboardPage = lazy(() =>
  import('@features/discharge/DischargeDashboardPage').then((m) => ({ default: m.DischargeDashboardPage })),
);
export const DischargeWorklistPage = lazy(() =>
  import('@features/discharge/DischargeWorklistPage').then((m) => ({ default: m.DischargeWorklistPage })),
);
export const DischargeDeskPage = lazy(() => import('@features/discharge/DischargeDeskPage').then((m) => ({ default: m.DischargeDeskPage })));
export const DischargeLabReportsPage = lazy(() =>
  import('@features/discharge/DischargeLabReportsPage').then((m) => ({ default: m.DischargeLabReportsPage })),
);
export const DischargeRegisterPage = lazy(() =>
  import('@features/discharge/DischargeRegisterPage').then((m) => ({ default: m.DischargeRegisterPage })),
);

// MRD MODULE (owner, 2026-11-02).
export const MrdDashboardPage = lazy(() => import('@features/mrd/MrdDashboardPage').then((m) => ({ default: m.MrdDashboardPage })));
export const MrdInboxPage = lazy(() => import('@features/mrd/MrdInboxPage').then((m) => ({ default: m.MrdInboxPage })));
export const MrdDeficienciesPage = lazy(() => import('@features/mrd/MrdDeficienciesPage').then((m) => ({ default: m.MrdDeficienciesPage })));
export const MrdCodingPage = lazy(() => import('@features/mrd/MrdCodingPage').then((m) => ({ default: m.MrdCodingPage })));
export const MrdIndexPage = lazy(() => import('@features/mrd/MrdIndexPage').then((m) => ({ default: m.MrdIndexPage })));
export const MrdCodeListPage = lazy(() => import('@features/mrd/MrdCodeListPage').then((m) => ({ default: m.MrdCodeListPage })));
export const MrdFileTrackerPage = lazy(() => import('@features/mrd/MrdFileTrackerPage').then((m) => ({ default: m.MrdFileTrackerPage })));
export const MrdArchivePage = lazy(() => import('@features/mrd/MrdArchivePage').then((m) => ({ default: m.MrdArchivePage })));
export const MrdRetentionPage = lazy(() => import('@features/mrd/MrdRetentionPage').then((m) => ({ default: m.MrdRetentionPage })));
export const MrdRequestsPage = lazy(() => import('@features/mrd/MrdRequestsPage').then((m) => ({ default: m.MrdRequestsPage })));
export const MrdCertificatesPage = lazy(() => import('@features/mrd/MrdCertificatesPage').then((m) => ({ default: m.MrdCertificatesPage })));
export const MrdIssueLogPage = lazy(() => import('@features/mrd/MrdIssueLogPage').then((m) => ({ default: m.MrdIssueLogPage })));
export const MrdBirthsPage = lazy(() => import('@features/mrd/MrdBirthsPage').then((m) => ({ default: m.MrdBirthsPage })));
export const MrdDeathsPage = lazy(() => import('@features/mrd/MrdDeathsPage').then((m) => ({ default: m.MrdDeathsPage })));
export const MrdMlcPage = lazy(() => import('@features/mrd/MrdMlcPage').then((m) => ({ default: m.MrdMlcPage })));
export const MrdOutcomesPage = lazy(() => import('@features/mrd/MrdOutcomesPage').then((m) => ({ default: m.MrdOutcomesPage })));
export const MrdNotifiablePage = lazy(() => import('@features/mrd/MrdIndexPage').then((m) => ({ default: m.MrdNotifiablePage })));
export const MrdStatisticsPage = lazy(() => import('@features/mrd/MrdStatisticsPage').then((m) => ({ default: m.MrdStatisticsPage })));
export const MrdSettingsPage = lazy(() => import('@features/mrd/MrdSettingsPage').then((m) => ({ default: m.MrdSettingsPage })));
